import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import StatusBadge from '../../../components/ui/StatusBadge';

const ChallengeCard = ({ challenge, onClick, isHighlighted, variant }) => {
  const progressPercentage = challenge?.totalGoals > 0
    ? Math.round((challenge?.completedProofs / challenge?.totalGoals) * 100)
    : 0;

  const getGoalTypeLabel = (type) => {
    const labels = {
      fixed: '고정 목표',
      split: '분할 목표',
      thirty_day: '30일 챌린지'
    };
    return labels?.[type] || type;
  };

  const getStatusBadge = () => {
    if (challenge?.isCompleted) {
      return (
        <StatusBadge
          label="완료"
          tone="completed"
          iconName="CheckCircle"
          rounded="full"
          className="bg-primary text-primary-foreground border-primary/30"
        />
      );
    }
    if (challenge?.isLocked) {
      return (
        <StatusBadge label="잠금" tone="locked" iconName="Lock" rounded="full" />
      );
    }
    return (
      <StatusBadge label="진행 중" tone="in_progress" iconName="Play" rounded="full" />
    );
  };

  if (variant === 'list') {
    return (
      <div
        onClick={onClick}
        className={`bg-card rounded-lg shadow-gaming-md overflow-hidden cursor-pointer transition-gaming hover:shadow-gaming-lg active:translate-y-px active:shadow-gaming-sm border border-border ${
          isHighlighted ? 'ring-2 ring-primary' : ''
        }`}
      >
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-56 h-40 md:h-auto overflow-hidden bg-muted">
            <Image
              src={challenge?.coverImage || '/assets/images/no_image.png'}
              alt={challenge?.coverImageAlt || challenge?.title || 'Challenge cover image'}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-3 left-3">{getStatusBadge()}</div>
            {challenge?.isLocked && (
              <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
                <div className="text-center px-4">
                  <Icon name="Lock" size={32} color="var(--color-muted-foreground)" />
                  <p className="text-xs text-muted-foreground mt-2 caption">이전 챌린지를 완료하세요</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 p-4 md:p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground line-clamp-2 flex-1">
                {challenge?.title}
              </h3>
              <span className="text-sm font-bold text-primary data-text flex-shrink-0">
                {progressPercentage}%
              </span>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {challenge?.description}
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="px-3 py-1 bg-muted rounded-full">
                <span className="text-xs font-medium text-foreground caption">
                  {getGoalTypeLabel(challenge?.goalType)}
                </span>
              </div>
              <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/20 border border-primary/30">
                {[...Array(5)]?.map((_, index) => (
                  <Icon
                    key={index}
                    name="Star"
                    size={14}
                    color="currentColor"
                    className={index < challenge?.averageStars ? 'fill-current text-primary' : 'text-muted-foreground'}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Target" size={16} color="var(--color-primary)" />
                <span className="text-xs text-muted-foreground caption">
                  목표 <span className="font-medium text-foreground data-text">{challenge?.totalGoals}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                <span className="text-xs text-muted-foreground caption">
                  완료 <span className="font-medium text-foreground data-text">{challenge?.completedProofs}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Heart" size={16} color="var(--color-destructive)" />
                <span className="text-xs text-muted-foreground caption">
                  좋아요 <span className="font-medium text-foreground data-text">{challenge?.totalLikes ?? 0}</span>
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-accent)" />
                <span className="text-xs text-muted-foreground caption">{challenge?.estimatedTime}</span>
              </div>
            </div>

            <div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-gaming rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'tall') {
    return (
      <div
        onClick={onClick}
        className={`bg-card rounded-lg shadow-gaming-md overflow-hidden cursor-pointer transition-gaming hover:shadow-gaming-lg active:translate-y-px active:shadow-gaming-sm border ${
          isHighlighted ? 'ring-2 ring-primary' : ''
        } ${challenge?.isCompleted ? 'border-primary/40 bg-primary/5' : 'border-border'}`}
      >
        <div className="relative h-56 md:h-64 overflow-hidden bg-muted">
          <Image
            src={challenge?.coverImage || '/assets/images/no_image.png'}
            alt={challenge?.coverImageAlt || challenge?.title || 'Challenge cover image'}
            className="w-full h-full object-cover"
          />
          <div className="absolute top-3 left-3">{getStatusBadge()}</div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className={challenge?.isCompleted ? 'bg-primary/90 rounded-md px-3 py-2 border border-primary/50' : 'bg-background/80 rounded-md px-3 py-2'}>
              <div className="flex items-center justify-between">
                <span className={challenge?.isCompleted ? 'text-xs text-primary-foreground/90 caption' : 'text-xs text-muted-foreground caption'}>진행률</span>
                <span className={challenge?.isCompleted ? 'text-xs font-bold text-primary-foreground data-text' : 'text-xs font-bold text-primary data-text'}>{progressPercentage}%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-2">
                <div
                  className="h-full bg-primary transition-gaming rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
          {challenge?.isLocked && (
            <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
              <div className="text-center px-4">
                <Icon name="Lock" size={32} color="var(--color-muted-foreground)" />
                <p className="text-xs text-muted-foreground mt-2 caption">이전 챌린지를 완료하세요</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="text-base font-heading font-semibold text-foreground line-clamp-2 mb-2">
            {challenge?.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {challenge?.description}
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="px-3 py-1 bg-muted rounded-full">
              <span className="text-xs font-medium text-foreground caption">
                {getGoalTypeLabel(challenge?.goalType)}
              </span>
            </div>
            <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/20">
              {[...Array(5)]?.map((_, index) => (
                <Icon
                  key={index}
                  name="Star"
                  size={14}
                  color="currentColor"
                  className={index < challenge?.averageStars ? 'fill-current text-primary' : 'text-muted-foreground'}
                />
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-muted-foreground caption">
              목표 <span className="font-medium text-foreground data-text">{challenge?.totalGoals}</span>
            </div>
            <div className="text-xs text-muted-foreground caption">
              완료 <span className="font-medium text-foreground data-text">{challenge?.completedProofs}</span>
            </div>
            <div className="text-xs text-muted-foreground caption">
              좋아요 <span className="font-medium text-foreground data-text">{challenge?.totalLikes ?? 0}</span>
            </div>
            <div className="text-xs text-muted-foreground caption">{challenge?.estimatedTime}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className={`bg-card rounded-lg shadow-gaming-md overflow-hidden cursor-pointer transition-gaming hover:shadow-gaming-lg active:translate-y-px active:shadow-gaming-sm border border-border ${
        isHighlighted ? 'ring-2 ring-primary' : ''
      }`}
    >
      <div className="relative h-56 md:h-64 overflow-hidden bg-muted">
        <Image
          src={challenge?.coverImage || '/assets/images/no_image.png'}
          alt={challenge?.coverImageAlt || challenge?.title || 'Challenge cover image'}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">{getStatusBadge()}</div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="bg-background/80 rounded-md px-3 py-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground caption">진행률</span>
              <span className="text-xs font-bold text-primary data-text">{progressPercentage}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden mt-2">
              <div
                className="h-full bg-primary transition-gaming rounded-full"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
        {challenge?.isLocked && (
          <div className="absolute inset-0 bg-background/90 flex items-center justify-center">
            <div className="text-center px-4">
              <Icon name="Lock" size={32} color="var(--color-muted-foreground)" />
              <p className="text-xs text-muted-foreground mt-2 caption">이전 챌린지를 완료하세요</p>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground line-clamp-2 flex-1">
            {challenge?.title}
          </h3>
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
          {challenge?.description}
        </p>

        <div className="flex items-center space-x-2 mb-4">
          <div className="px-3 py-1 bg-muted rounded-full">
            <span className="text-xs font-medium text-foreground caption">
              {getGoalTypeLabel(challenge?.goalType)}
            </span>
          </div>
          <div className="flex items-center space-x-1 px-2 py-1 rounded-full bg-primary/20 border border-primary/30">
            {[...Array(5)]?.map((_, index) => (
              <Icon
                key={index}
                name="Star"
                size={14}
                color="currentColor"
                className={index < challenge?.averageStars ? 'fill-current text-primary' : 'text-muted-foreground'}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={16} color="var(--color-primary)" />
            <span className="text-xs md:text-sm text-muted-foreground caption">
              목표: <span className="font-medium text-foreground data-text">{challenge?.totalGoals}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
            <span className="text-xs md:text-sm text-muted-foreground caption">
              완료: <span className="font-medium text-foreground data-text">{challenge?.completedProofs}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Heart" size={16} color="var(--color-destructive)" />
            <span className="text-xs md:text-sm text-muted-foreground caption">
              좋아요: <span className="font-medium text-foreground data-text">{challenge?.totalLikes ?? 0}</span>
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} color="var(--color-accent)" />
            <span className="text-xs md:text-sm text-muted-foreground caption">
              {challenge?.estimatedTime}
            </span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs md:text-sm text-muted-foreground caption">진행률</span>
            <span className="text-xs md:text-sm font-bold text-primary data-text">
              {progressPercentage}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-gaming rounded-full"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {challenge?.recentProofs && challenge?.recentProofs?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Image" size={14} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground caption">최근 증명</span>
            </div>
            <div className="flex items-center space-x-2 overflow-x-auto">
              {challenge?.recentProofs?.map((proof, index) => (
                <div key={index} className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-md overflow-hidden">
                  {proof?.image ? (
                    <Image
                      src={proof?.image}
                      alt={proof?.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeCard;